// The contextual inline menu that reveals on hover/focus over an editable post section
// (caption, hashtags, message, title, description). Mirrors the canvas FloatingToolbar's
// "power on hover" feel with the same dark T3 chrome, so every section edits the same way:
// hover → a small toolbar of the actions relevant to that section. Desktop only (mobile keeps
// its tap→sheet flow); hidden on live/read-only posts.

import { useState, type ReactNode } from 'react'
import { Check, PenLine } from 'lucide-react'
import { useComposerReadOnly } from '../components/composer-read-only'
import { useVariantMode } from '../components/variant-mode'
import { GLOSSY_PILL, GLOSSY_TEXT_BTN } from '../composition/editor/toolbar-style'

export interface SectionAction {
  icon: ReactNode
  label: string
  onClick: () => void
  /** When set, the button briefly swaps to this label + a check after a click (e.g. "Copied"). */
  confirmLabel?: string
}

function ActionButton({ action }: { action: SectionAction }) {
  const [confirmed, setConfirmed] = useState(false)
  // Actions with a confirmLabel (e.g. Copy) render icon-only and flash the confirm word on
  // click; actions without one (e.g. Ask Taka) keep their label. Icons over words where we can.
  const label = confirmed ? action.confirmLabel : action.confirmLabel ? null : action.label
  return (
    <button
      type="button"
      title={action.label}
      aria-label={action.label}
      onClick={e => {
        e.stopPropagation()
        action.onClick()
        if (action.confirmLabel) {
          setConfirmed(true)
          setTimeout(() => setConfirmed(false), 1200)
        }
      }}
      className={`h-7 px-1.5 text-[11px] font-medium ${GLOSSY_TEXT_BTN}`}
    >
      {confirmed ? <Check size={12} className="text-emerald-400" /> : action.icon}
      {label && <span>{label}</span>}
    </button>
  )
}

export function SectionActionBar({
  actions,
  children,
  className,
}: {
  actions: SectionAction[]
  children: ReactNode
  className?: string
}) {
  const readOnly = useComposerReadOnly()
  const variant = useVariantMode()
  if (readOnly || actions.length === 0) return <>{children}</>

  // One Post model, platform tab with auto-adapted copy: the text is inert
  // behind the explicit customize gate — hovering explains the contract and
  // offers the one action. (Customized tabs fall through to normal editing.)
  if (variant && !variant.customized) {
    return (
      <div className={`group/section relative ${className ?? ''}`}>
        <div className="pointer-events-none">{children}</div>
        <div className="absolute inset-0 z-10 hidden flex-col items-center justify-center gap-2 rounded-[6px] bg-black/50 opacity-0 transition-all duration-200 group-hover/section:opacity-100 md:flex">
          <span className="text-[12px] font-medium text-white/90">Auto-adapted from your post</span>
          <button
            type="button"
            onClick={e => {
              e.stopPropagation()
              variant.onCustomizeRequest()
            }}
            className="flex cursor-pointer items-center gap-1.5 rounded-full bg-white/90 px-3 py-1.5 text-xs font-medium text-gray-800 shadow-lg transition-all duration-150 hover:scale-105 hover:bg-white"
          >
            <PenLine size={14} />
            Customize for {variant.platformLabel}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className={`group/section relative ${className ?? ''}`}>
      {children}
      <div
        data-section-toolbar
        className={`pointer-events-none absolute top-1.5 right-2 z-20 hidden items-center gap-0.5 px-1 py-0.5 opacity-0 transition-opacity duration-150 group-focus-within/section:pointer-events-auto group-focus-within/section:opacity-100 group-hover/section:pointer-events-auto group-hover/section:opacity-100 md:flex ${GLOSSY_PILL}`}
      >
        {actions.map(a => (
          <ActionButton key={a.label} action={a} />
        ))}
      </div>
    </div>
  )
}
